<template>
  <div class="documents-page">
    <template v-if="$route.name === 'Documents'">
      <div v-if="!isMobile" class="pt-4 mb-4">
        <Breadcrumbs/>
      </div>
      <div class="documents-manager mb-2">
        <div class="documents-list-container">
          <div
            v-if="!isMobile"
            class="dropzone"
            :class="{ 'is-dragover': isDragover }"
            @dragover.prevent="isDragover = true"
            @dragleave.prevent="isDragover = false"
            @drop.prevent="onFileDrop"
            @click="$refs.fileInput.click()"
          >
            <input
              type="file"
              ref="fileInput"
              class="hidden-input"
              accept=".pdf,.png,.jpg,.jpeg"
              @change="onFileSelect"
            >
            <div class="dropzone-content">
              <img src="@/assets/icons/upload-icon.svg" alt="upload">
              <div>
                <p class="dropzone-title">Drop files here or click to upload</p>
                <p class="dropzone-hint">PDF, PNG or JPG (max. 2MB)</p>
              </div>
            </div>
          </div>
          <div class="documents-title">DOCUMENTS</div>
          <v-card elevation="0" class="documents-list-wrapper">
            <div class="custom-table-header">
              <div class="table-col col-name">FILE NAME</div>
              <div class="table-col col-type">FILE TYPE</div>
              <div class="table-col col-date">UPLOAD DATE</div>
              <div class="table-col col-options text-right">OPTIONS</div>
            </div>
            <div class="custom-table-body">
              <div
                v-for="doc in documents"
                :key="doc.id"
                class="table-row"
                :class="{'active-row': activeDocId === doc.id}"
                @click="handleDocumentClick(doc)"
              >
                <div class="table-body-col col-name">
                  <img src="@/assets/icons/document-icon.svg" alt="document">
                  <span class="text-truncate">{{ doc.fileName }}</span>
                </div>
                <div class="table-body-col col-type">
                  <span class="type-badge">{{ doc.hasImportSession ? 'Lab report' : 'Document' }}</span>
                </div>
                <div class="table-body-col col-date">
                  {{ $moment(doc.createdOn).format('MM/DD/YYYY HH:mm') }}
                </div>
                <div class="table-body-col col-options" @click.stop>
                  <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" :ripple="false" class="options-btn">
                        <v-icon color="#A0AEC0">mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="downloadSingle(doc)">
                        <v-list-item-icon>
                          <img src="@/assets/icons/download-icon.svg" alt="download">
                        </v-list-item-icon>
                        <v-list-item-title>Download</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="doc && doc.canDelete"
                        @click="handleDelete(doc.id)"
                      >
                        <v-list-item-icon>
                          <img src="@/assets/icons/delete-icon.svg" alt="delete">
                        </v-list-item-icon>
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>

              <div v-if="!documents.length" class="no-data">
                <p>No documents found</p>
              </div>
            </div>
          </v-card>
        </div>
        <div class="session-detail-preview" v-if="!isMobile || activeDocId">
          <div v-if="isMobile && activeDocId" class="mobile-preview-bar">
            <button class="icon-btn" @click="activeDocId = null">
              <img src="@/assets/icons/btn-back-icon.svg" alt="back">
            </button>
            <div class="mobile-doc-name">
              {{ activeDocName }}
            </div>
            <div class="mobile-actions-right">
              <button
                v-if="activeDoc && activeDoc.canDelete"
                class="icon-btn"
                @click="mobileMenuSheet = true"
              >
                <v-icon color="#718096">mdi-dots-vertical</v-icon>
              </button>
              <button
                v-else
                class="icon-btn icon-btn-download"
                @click="downloadSingle(activeDoc)"
              >
                <img src="@/assets/icons/download-icon.svg" alt="download">
              </button>
            </div>
          </div>

          <div v-if="activeDocId && !loading" class="pdf-canvas-render">
            <vue-pdf-embed
              v-if="isPdf"
              :key="previewFileUrl"
              :source="previewFileUrl"
              class="pdf-render-surface"
            />
            <img v-else :src="previewFileUrl" class="img-preview-modal"/>
          </div>
          <div v-else-if="!isMobile" class="preview-placeholder">
            <span>Select a document to see preview</span>
          </div>
        </div>
      </div>
    </template>

    <div v-if="loading" class="loading d-flex justify-center px-2 py-2 align-center">
      <v-progress-circular
        indeterminate
        color="#008C89"
      ></v-progress-circular>
    </div>

    <v-bottom-sheet v-model="mobileMenuSheet">
      <v-list class="mobile-action-sheet">

        <v-list-item @click="downloadSingle(activeDoc); mobileMenuSheet = false">
          <v-list-item-icon>
            <img src="@/assets/icons/download-icon.svg" alt="download">
          </v-list-item-icon>
          <v-list-item-title>Download</v-list-item-title>
        </v-list-item>

        <v-list-item
          v-if="activeDoc && activeDoc.canDelete"
          @click="handleDelete(activeDocId); mobileMenuSheet = false">
          <v-list-item-icon>
            <img src="@/assets/icons/delete-icon-red.svg" alt="delete">
          </v-list-item-icon>
          <v-list-item-title>Delete</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-bottom-sheet>

    <v-btn
      v-if="isMobile && !activeDocId"
      fab
      dark
      fixed
      bottom
      right
      color="#008C89"
      class="mobile-fab"
      :loading="loading"
      @click="$refs.fileInput.click()"
    >
      <img src="@/assets/icons/plus-icon.svg" alt="plus">
    </v-btn>
    <div class="custom-alert">
      <v-alert :color="!isErrorMessage ? '#7ac1fb' : 'rgb(207, 70, 85)'" dismissible type="info" v-model="alertIsOpen">
        <span style="font-size: 14px">{{ popupMessage }}</span>
      </v-alert>
    </div>
    <confirm v-if="$store.state.dialogs.confirm.isOpen"/>
  </div>
</template>

<script>
import {mapActions, mapState, mapMutations} from 'vuex'
import Breadcrumbs from '@/components/layouts/Breadcrumbs'
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'
import confirm from '@/components/layouts/dialogs/confirm-dialog'

export default {
  name: 'documents',
  components: {Breadcrumbs, VuePdfEmbed, confirm},

  data: () => ({
    windowWidth: window.innerWidth,
    isDragover: false,
    activeDocId: null,
    activeDocName: '',
    previewFileUrl: null,
    alertTimeout: null,
    popupMessage: '',
    isErrorMessage: false,
    alertIsOpen: false,
    loading: false,
    mobileMenuSheet: false
  }),

  computed: {
    ...mapState('documents', {
      documents: state => state.patientDocuments || []
    }),
    ...mapState({
      userProfile: state => state.userProfile
    }),
    isMobile() {
      return this.windowWidth <= 960
    },
    isPdf() {
      if (!this.activeDocName) return false
      return this.activeDocName.toLowerCase().endsWith('.pdf')
    },
    activeDoc() {
      return this.documents.find(d => d.id === this.activeDocId)
    }
  },
  async mounted() {
    document.title = 'Documents - Maestro Patient Portal'
    await this.fetchDocs()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    ...mapActions('documents', [
      'getPatientDocuments',
      'uploadPatientDocument',
      'downloadDocumentFile',
      'removePatientDocument']
    ),
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    showAlert(message, isError = false) {
      clearTimeout(this.alertTimeout)

      this.popupMessage = message
      this.isErrorMessage = isError
      this.alertIsOpen = true

      this.alertTimeout = setTimeout(() => {
        this.alertIsOpen = false
      }, 2000)
    },
    async fetchDocs() {
      const patientId = this.userProfile?.userId

      if (!patientId) {
        console.warn('Patient ID is not available yet')
        return
      }

      this.loading = true
      try {
        await this.getPatientDocuments(patientId)
      } catch (error) {
        console.error('Error fetching documents:', error)
        this.showAlert('Failed to load documents.', true)
      } finally {
        this.loading = false
      }
    },
    onFileSelect(e) {
      const file = e.target.files[0]
      if (file) this.handleUpload(file)
      e.target.value = ''
    },
    onFileDrop(e) {
      this.isDragover = false
      const file = e.dataTransfer.files[0]
      if (file) this.handleUpload(file)
    },
    async handleUpload(file) {
      if (!file) return

      const maxFileSize = 2 * 1024 * 1024

      if (file.size > maxFileSize) {
        this.showAlert('File is too large. Max size is 2MB.', true)
        return
      }

      const patientId = this.userProfile?.userId
      if (!patientId) return

      this.loading = true

      try {
        const res = await this.uploadPatientDocument({
          file,
          patientId
        })

        if (!res?.success) {
          this.showAlert('Failed to upload document.', true)
          return
        }

        await this.fetchDocs()
        if (res.document) {
          await this.handleDocumentClick(res.document)
        }

        this.loading = false
        await this.$nextTick()

        this.showAlert('Document uploaded successfully.')
      } catch (error) {
        console.error('Upload error:', error)
        this.showAlert('Failed to upload document. Please try again.', true)
      } finally {
        this.loading = false
      }
    },

    async handleDocumentClick(doc) {
      if (this.activeDocId === doc.id) return

      this.activeDocId = doc.id
      this.activeDocName = doc.fileName

      this.loading = true

      try {
        if (this.previewFileUrl) URL.revokeObjectURL(this.previewFileUrl)

        const blobData = await this.downloadDocumentFile({id: doc.id})

        if (!blobData) {
          this.showAlert('Failed to load document preview.', true)
          return
        }

        this.previewFileUrl = URL.createObjectURL(new Blob([blobData]))
      } catch (error) {
        console.error(error)
        this.showAlert('Failed to load document preview.', true)
      } finally {
        this.loading = false
      }
    },

    async downloadSingle(doc) {
      try {
        const blobData = await this.downloadDocumentFile({id: doc.id})

        if (!blobData) {
          this.showAlert('Failed to download document.', true)
          return
        }

        const url = window.URL.createObjectURL(new Blob([blobData]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', doc.fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()

        URL.revokeObjectURL(url)

        // this.showAlert('Document downloaded successfully.')
      } catch (error) {
        console.error(error)
        this.showAlert('Failed to download document.', true)
      }
    },
    async handleDelete(id) {
      this.TOGGLE_DIALOG({
        name: 'confirm',
        isOpen: true,
        title: 'Confirm Deletion',
        description: 'Are you sure you want to delete this document?',

        callback: async () => {
          this.TOGGLE_DIALOG({name: 'confirm', isOpen: false})

          this.loading = true

          try {
            const res = await this.removePatientDocument(id)

            if (!res?.success) {
              this.showAlert('Failed to delete document.', true)
              return
            }

            if (this.activeDocId === id) {
              this.activeDocId = null

              if (this.previewFileUrl) {
                URL.revokeObjectURL(this.previewFileUrl)
                this.previewFileUrl = null
              }
            }

            await this.fetchDocs()

            this.loading = false
            await this.$nextTick()

            this.showAlert('Document deleted successfully.')
          } catch (error) {
            console.error(error)
            this.showAlert('A server error occurred while deleting document.', true)
          } finally {
            this.loading = false
          }
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
$color-bg: #FFFFFF;
$color-text-primary: #1A202C;
$color-text-secondary: #718096;
$radius-md: 8px;
$transition-base: 0.2s ease;
$grid-template: 2.5fr 1.2fr 1.5fr 70px;
$mobile-breakpoint: 960px;

@mixin flex-center {
  display: flex;
  align-items: center;
}

.documents-page {
  position: relative;
  height: calc(100vh - 57px);
  padding: 0 16px;
  background-color: #F7FAFC;
}

.documents-manager {
  display: grid;
  grid-template-columns: 574px minmax(0, 1fr);
  gap: 42px;
  align-items: start;
}

.documents-list-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.dropzone {
  flex: 1 1 0;
  min-width: 0;
  height: 132px;
  @include flex-center;
  justify-content: center;
  border: 1px dashed #9FB6FF;
  background-color: rgba(208, 220, 255, 0.3);
  border-radius: $radius-md;
  padding: 28px 15px;
  margin-bottom: 27px;
  cursor: pointer;
  transition: all $transition-base;

  .dropzone-content {
    @include flex-center;
    column-gap: 40px;
    font-size: 16px;
    font-weight: 400;

    svg {
      flex-shrink: 0;
      width: 50px;
    }
  }

  &-title {
    color: $color-text-primary;
    margin-bottom: 4px;
  }

  &-hint {
    color: $color-text-secondary;
    margin-bottom: 0;
  }
}

.documents-title {
  color: $color-text-secondary;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.documents-list-wrapper {
  border-radius: 0;
  box-shadow: none !important;
  margin-top: 12px;

  .custom-table-header {
    height: 52px;
    display: grid;
    grid-template-columns: $grid-template;
    column-gap: 1px;
    border-bottom: 1px solid #EDF2F7;

    .table-col {
      @include flex-center;
      color: #8492A6;
      font-size: 10px;
      font-weight: 600;
      line-height: 16px;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      background-color: $color-bg;
      padding-left: 14px;

      &:first-child {
        padding-left: 24px;
      }

      &:last-child {
        padding-left: 0;
        padding-right: 17px;
        justify-content: flex-end;
      }
    }
  }

  .custom-table-body {
    .table-row {
      min-height: 52px;
      display: grid;
      grid-template-columns: $grid-template;
      column-gap: 1px;
      align-items: center;
      border-bottom: 1px solid #F2F4F7;
      cursor: pointer;

      &:hover:not(:has(.options-btn:hover)),
      &.active-row {
        .table-body-col {
          background-color: #E5F3F3;
        }
      }

      .table-body-col {
        @include flex-center;
        height: 100%;
        color: $color-text-primary;
        font-size: 15px;
        font-weight: 400;
        line-height: 20px;
        background-color: $color-bg;
        padding-left: 12px;

        &:first-child {
          padding-left: 20px;
        }
      }

      .col-name {
        column-gap: 8px;
        font-weight: 600;
        min-width: 0;
      }

      .col-options {
        padding-left: 0;
        padding-right: 13px;
        justify-content: flex-end;

        .options-btn {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          transition: background-color $transition-base;

          &:hover {
            background-color: #EDF2F7;
          }

          &::before {
            display: none;
          }
        }
      }
    }
  }

  .no-data {
    color: #98A2B3;
    text-align: center;
    background-color: $color-bg;
    border-radius: 0 0 $radius-md $radius-md;
    padding: 48px;
  }
}

.session-detail-preview {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  height: calc(100vh - 150px);

  .mobile-preview-bar {
    @include flex-center;
    justify-content: flex-start;
    column-gap: 15px;
    height: 96px;
    padding: 25px 20px;
    background-color: $color-bg;
    border-bottom: 1px solid #EDF2F7;
    flex-shrink: 0;

    .mobile-actions-right {
      margin-left: auto;
    }

    .icon-btn {
      @include flex-center;
      justify-content: center;
      width: 46px;
      height: 46px;

      &.icon-btn-download {
        img {
          width: 24px;
          height: 24px;
        }
      }
    }

    .mobile-doc-name {
      flex: 1;
      color: #16192C;
      font-size: 18px;
      font-weight: 600;
      line-height: 27px;
      letter-spacing: -0.5%;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-break: break-all;
      overflow: hidden;
    }
  }

  .pdf-canvas-render {
    width: 100%;
    flex: 1;
    @include flex-center;
    flex-direction: column;
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;

    .pdf-render-surface {
      width: 100%;
      box-sizing: border-box;

      ::v-deep canvas {
        display: block;
        width: 100% !important;
        height: auto !important;
        max-width: 100% !important;
        box-shadow: 0px 3px 8px -1px rgba(50, 50, 71, 0.05),
        0px 0px 1px 0px rgba(12, 26, 75, 0.24);
        margin: 0 auto 20px auto;
      }
    }

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background-color: #f1f5f9;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #cbd5e0;
      border-radius: 10px;
      border: 2px solid #f1f5f9;
    }
  }

  .preview-placeholder {
    flex: 1;
    @include flex-center;
    flex-direction: column;
    justify-content: center;
    background-color: $color-bg;
    border: 1px dashed #e2e8f0;
    border-radius: $radius-md;

    span {
      color: #A0AEC0;
      font-size: 16px;
      font-weight: 400;
      margin-top: 12px;
    }
  }
}

.v-menu__content {
  min-width: 159px !important;
  box-shadow: 0px 20px 24px 0px rgba(20, 37, 63, 0.06),
  0px 0px 1px 0px rgba(12, 26, 75, 0.1);
  border-radius: $radius-md !important;

  .v-list {
    display: flex;
    flex-direction: column;
    row-gap: 6px;
    padding: 12px;

    .v-list-item {
      @include flex-center;
      column-gap: 12px;
      padding: 10px 10px 8px 11px !important;
      min-height: 34px !important;

      &::before,
      &::after {
        content: none;
      }

      .v-list-item__icon {
        min-width: 16px;
        width: 16px;
        height: 16px;
        margin: 0;
      }

      .v-list-item__title {
        color: #425466;
        font-size: 14px;
        font-weight: 500;
        line-height: 14px;
        padding: 2px 0 0;
      }
    }
  }
}

.mobile-action-sheet {
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  border-radius: 16px 16px 0 0 !important;
  padding: 40px 0 25px;

  .v-list-item {
    display: flex;
    align-items: center;
    column-gap: 20px;
    min-width: 170px;
    padding: 10px 20px !important;
    margin: 0 auto;

    &::before,
    &::after {
      content: none;
    }

    &__icon {
      align-self: flex-start;
      margin: 0;

      img {
        width: 24px;
        height: 24px;
      }
    }

    &__title {
      font-size: 16px !important;
      font-weight: 600;
    }
  }
}

.loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(213, 213, 213, 0.5);
  z-index: 999;
}

.hidden-input {
  display: none;
}

.custom-alert {
  max-width: 300px;
  position: fixed;
  top: 60px;
  right: 10px;
  z-index: 100;
}

@media screen and (max-width: $mobile-breakpoint) {
  .documents-page {
    padding: 0;
  }

  .session-detail-preview {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: $color-bg;
    margin: 0;
    padding: 0;
    z-index: 200;

    .pdf-canvas-render {
      height: calc(100vh - 96px);
      border: none;
      border-radius: 0;
    }
  }

  .documents-manager {
    display: block;
  }

  .documents-title {
    display: none;
  }

  .documents-list-wrapper {
    margin-top: 4px;

    .custom-table-header {
      display: none;
    }

    .custom-table-body {
      .table-row {
        display: grid;
        grid-template-columns: 1fr auto;
        grid-template-areas:
          "name date"
          "type .";
        padding: 20px 10px;
        height: auto;
        min-height: 85px;
        gap: 5px 20px;
        background-color: $color-bg;

        &:hover,
        &.active-row {
          .table-body-col {
            background-color: transparent !important;
          }
        }

        .table-body-col {
          padding-left: 0 !important;
        }

        .col-name {
          grid-area: name;
          font-size: 16px !important;

          img {
            display: none;
          }
        }

        .col-type {
          grid-area: type;
          justify-content: flex-start;
          color: $color-text-secondary;
        }

        .col-date {
          grid-area: date;
          color: #2D3748;
          font-size: 14px;
        }

        .col-options {
          display: none;
        }
      }
    }
  }

  .mobile-fab {
    bottom: 57px;
    right: 15px;
    z-index: 10;
    box-shadow: none;
  }


  .loading {
    position: absolute;
    top: -64px;
    left: 0;
    width: 100%;
    height: calc(100% + 64px);
    background-color: rgba(213, 213, 213, 0.5);
    z-index: 999;
  }
}
</style>
